/**
 * Central SEO configuration + helpers.
 *
 * One source of truth for the marketing-site metadata that several files need
 * to agree on: the root layout metadata, route-level metadata, robots.ts,
 * sitemap.ts, the OG image route, and the JSON-LD structured data. Keep this
 * dependency-free and side-effect-free so it can be imported from server
 * components, route handlers, and the SEO smoke test alike.
 *
 * NOTE: this file governs INDEX HYGIENE only (what crawlers see). It must not
 * influence auth, routing, or governance. The private-route list here mirrors
 * the `X-Robots-Tag` headers in next.config.mjs (the robust enforcement); the
 * `test:seo` smoke cross-checks that the two stay in lockstep.
 */

export const SITE_NAME = "FixControl";

/**
 * Canonical public origin. Driven by NEXT_PUBLIC_SITE_URL so prod
 * (https://fixcontrol.ai) and any preview origin resolve correctly; falls back
 * to the production host. Trailing slash stripped so absoluteUrl() can join
 * cleanly.
 */
export const DEFAULT_SITE_URL = (
  process.env.NEXT_PUBLIC_SITE_URL || "https://fixcontrol.ai"
)
  .trim()
  .replace(/\/+$/, "");

/** Build an absolute URL for a site-relative path (e.g. "/demo"). */
export function absoluteUrl(path = "/"): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${DEFAULT_SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

/** Title template applied site-wide via the root layout metadata. */
export const TITLE_TEMPLATE = `%s · ${SITE_NAME}`;

/**
 * Default site description. V5 positioning (context × governance pair, plain
 * language), deliberately not over-claiming autonomy: every action is
 * human-approved and auditable. Kept in step with the homepage metadata.
 */
export const DEFAULT_DESCRIPTION =
  "FixControl starts from a customer issue or an engineering bug, investigates it with your documentation and code, proposes a fix and tests it in an isolated sandbox — delivered as a pull request, by default only after a recorded approval.";

/** Default keyword set. Next supports `keywords` as a metadata field. */
export const DEFAULT_KEYWORDS = [
  "AI governance for engineering operations",
  "support-to-engineering workflow",
  "human approval gates",
  "Slack approvals",
  "human-in-the-loop AI",
  "audit trail",
  "production remediation",
  "deployment governance",
  "tenant isolation",
  "Freshdesk",
  "Jira",
  "Linear",
  "GitHub",
  "GitLab",
  "Argo",
  "Kubernetes",
];

/** Site-relative path of the dynamically generated Open Graph image. */
export const OG_IMAGE_PATH = "/opengraph-image";

export const OG_IMAGE = {
  url: absoluteUrl(OG_IMAGE_PATH),
  width: 1200,
  height: 630,
  alt: "FixControl — From issue to tested fix: from a customer report or an engineering bug, investigated, tested in a sandbox, approved by your team",
};

/**
 * Public, indexable marketing routes. These (and only these) belong in the
 * sitemap. `signup` is treated as a public acquisition route; flip its
 * `index` flag here if that ever changes.
 */
export const PUBLIC_ROUTES: ReadonlyArray<{
  path: string;
  priority: number;
  changeFrequency:
    | "always"
    | "hourly"
    | "daily"
    | "weekly"
    | "monthly"
    | "yearly"
    | "never";
}> = [
  { path: "/", priority: 1.0, changeFrequency: "weekly" },
  { path: "/product", priority: 0.9, changeFrequency: "monthly" },
  { path: "/pricing", priority: 0.8, changeFrequency: "monthly" },
  { path: "/business-value", priority: 0.8, changeFrequency: "monthly" },
  { path: "/demo", priority: 0.8, changeFrequency: "monthly" },
  // Use-case landing pages — the primary SEO surface for the product workflows.
  { path: "/use-cases", priority: 0.7, changeFrequency: "monthly" },
  { path: "/use-cases/support", priority: 0.8, changeFrequency: "monthly" },
  { path: "/use-cases/engineering", priority: 0.8, changeFrequency: "monthly" },
  { path: "/use-cases/slack-jira-ticket-questions", priority: 0.8, changeFrequency: "monthly" },
  { path: "/use-cases/freshdesk-jira-approved-replies", priority: 0.8, changeFrequency: "monthly" },
  { path: "/use-cases/support-to-engineering-remediation", priority: 0.8, changeFrequency: "monthly" },
  { path: "/use-cases/ai-agent-approval-ledger", priority: 0.8, changeFrequency: "monthly" },
  { path: "/use-cases/ai-deployment-governance", priority: 0.8, changeFrequency: "monthly" },
  { path: "/use-cases/hr-governed-answers", priority: 0.8, changeFrequency: "monthly" },
  // Integration landing pages. NOTE: the OAuth link-confirm flows under
  // /integrations/<provider>/link stay noindex (see PRIVATE_ROUTE_PREFIXES);
  // only these marketing roots are indexable.
  { path: "/integrations", priority: 0.7, changeFrequency: "monthly" },
  { path: "/integrations/slack", priority: 0.7, changeFrequency: "monthly" },
  { path: "/integrations/teams", priority: 0.7, changeFrequency: "monthly" },
  { path: "/integrations/jira", priority: 0.7, changeFrequency: "monthly" },
  { path: "/integrations/freshdesk", priority: 0.7, changeFrequency: "monthly" },
  { path: "/integrations/odoo", priority: 0.7, changeFrequency: "monthly" },
  { path: "/integrations/topdesk", priority: 0.7, changeFrequency: "monthly" },
  { path: "/integrations/afas", priority: 0.7, changeFrequency: "monthly" },
  { path: "/integrations/youforce", priority: 0.7, changeFrequency: "monthly" },
  { path: "/integrations/github", priority: 0.7, changeFrequency: "monthly" },
  { path: "/integrations/gitlab", priority: 0.7, changeFrequency: "monthly" },
  { path: "/integrations/argo", priority: 0.7, changeFrequency: "monthly" },
  { path: "/integrations/fc-agent", priority: 0.7, changeFrequency: "monthly" },
  { path: "/product/change-validation", priority: 0.7, changeFrequency: "monthly" },
  // Comparison pages — where "isn't this just n8n / search / ServiceNow?"
  // search traffic lands (V4.4 boundaries triptych).
  { path: "/compare/workflow-automation", priority: 0.6, changeFrequency: "monthly" },
  { path: "/compare/enterprise-search", priority: 0.6, changeFrequency: "monthly" },
  { path: "/compare/servicenow", priority: 0.6, changeFrequency: "monthly" },
  { path: "/signup", priority: 0.7, changeFrequency: "monthly" },
  { path: "/security", priority: 0.7, changeFrequency: "monthly" },
  { path: "/about", priority: 0.6, changeFrequency: "monthly" },
  { path: "/contact", priority: 0.6, changeFrequency: "monthly" },
  { path: "/terms", priority: 0.3, changeFrequency: "yearly" },
  { path: "/privacy", priority: 0.3, changeFrequency: "yearly" },
];

/**
 * Private / internal route prefixes that must NOT be indexed. Includes
 * operator surfaces, the authenticated app, and tenant/customer-specific links
 * (intake slugs, share-link viewer `/v`). Robustly enforced by the
 * `X-Robots-Tag: noindex, nofollow` headers in next.config.mjs; this list is
 * the canonical source the smoke test checks both files against.
 *
 * Each entry covers the bare path and everything beneath it.
 */
export const PRIVATE_ROUTE_PREFIXES: readonly string[] = [
  "/admin",
  "/agents",
  "/app",
  "/follow-ups",
  "/governance",
  "/inbox",
  // Only the OAuth link-confirm flows under /integrations are private; the
  // /integrations/<provider> marketing roots are public + indexable.
  "/integrations/slack/link",
  "/integrations/teams/link",
  "/intake",
  "/issues",
  "/knowledge",
  "/login",
  "/forgot-password",
  "/reset-password",
  "/memory",
  "/missions",
  "/notifications",
  "/operations",
  "/orchestration",
  "/patches",
  "/projects",
  "/runs",
  "/sandbox-runs",
  "/settings",
  "/tenants",
  // Mid-login 2FA enrollment surface (see proxy.ts) — authenticated, never
  // meaningful to a crawler.
  "/two-factor-setup",
  "/v",
  "/workspace",
];

/**
 * Hosts allowed to serve indexable content. An explicit allowlist (not a
 * blocklist) so any new dev/staging/preview host is noindex by default — you
 * never have to remember to add it. This is the RUNTIME source of truth.
 */
export const INDEXABLE_HOSTS = ["fixcontrol.ai", "www.fixcontrol.ai"];

/** Strip the port and lowercase, so "Fixcontrol.ai:443" → "fixcontrol.ai". */
export function normalizeHost(host: string | null | undefined): string {
  return String(host || "")
    .split(":")[0]
    .toLowerCase();
}

/**
 * Leading runtime index decision: only the exact production hosts may be
 * indexed. Everything else — dev.fixcontrol.ai, staging.fixcontrol.ai, preview
 * deploys, localhost, 127.0.0.1, unknown hosts — is noindex. Independent of
 * build-time NEXT_PUBLIC_SITE_URL, so a single build artifact is safe to run
 * on any host.
 */
export function isIndexableHost(host: string | null | undefined): boolean {
  return INDEXABLE_HOSTS.includes(normalizeHost(host));
}

/**
 * Build-time heuristic retained for non-request contexts (e.g. tooling). For
 * any request-driven decision (robots, sitemap, middleware) prefer
 * `isIndexableHost()`, which reads the actual request host at runtime.
 */
export function isNonProductionHost(): boolean {
  if (process.env.NODE_ENV !== "production") return true;
  const url = DEFAULT_SITE_URL.toLowerCase();
  return (
    url.includes("dev.") ||
    url.includes("staging") ||
    url.includes("localhost") ||
    url.includes("127.0.0.1")
  );
}
